import React, { useState } from 'react';

const STEPS = [
  {
    id: 1,
    title: 'Welcome to RoofLeads Pro!',
    icon: '🏠',
    description: 'The #1 lead generation tool for roofing contractors. Find storm-damaged properties, wealthy homeowners, and hot leads in seconds.',
    features: [
      '⚡ Real-time storm damage tracking from NOAA',
      '🔥 Pre-scored hot leads with owner data',
      '💰 Property values & roof estimates',
      '📋 Built-in CRM to track your leads'
    ],
    tip: 'This quick tour will show you how to find and close more roofing jobs!',
    category: 'intro'
  },
  {
    id: 2,
    title: 'Find Storm-Damaged Areas',
    icon: '⚡',
    description: 'Click the "Storms" button in the header to see recent hail, wind, and tornado events. Properties in storm zones are your hottest leads!',
    features: [
      'Click "⚡ Storms" in the top navigation',
      'Filter by Hail, Wind, or Tornado',
      'Click "Go to area →" to navigate to a storm zone',
      'Sync NOAA to get the latest storm data'
    ],
    tip: 'Storm-affected properties are 3x more likely to need roof replacement!',
    highlight: 'storms-btn',
    category: 'storms'
  },
  {
    id: 3,
    title: 'Use Quick Navigation',
    icon: '📍',
    description: 'The Storm Zones panel on the left shows pre-researched hot areas in Indianapolis with recent hail damage and wealthy neighborhoods.',
    features: [
      '⚡ Storm Zones - Areas with confirmed hail damage',
      '💰 Wealthy Areas - High-income neighborhoods',
      'Click any button to instantly navigate there',
      'Each area shows hail size and date of damage'
    ],
    tip: 'Sunshine Gardens had 0.75" hail in March 2025 - perfect for canvassing!',
    highlight: 'storm-quick-nav',
    category: 'navigation'
  },
  {
    id: 4,
    title: 'Use the Map Filters',
    icon: '🎯',
    description: 'The filter buttons at the top-left of the map help you focus on the best leads. Toggle them on/off to customize your view.',
    features: [
      '⚡ Storms - Show/hide storm overlay heatmap',
      '💰 Rich - Highlight properties over $300K',
      '🔥 Hot - Show only high-score leads',
      '🏠 Big Roof - Properties with 2500+ sqft roofs'
    ],
    tip: 'Combine "Storms" + "Rich" to find wealthy homeowners in storm zones!',
    highlight: 'map-filters',
    category: 'filters'
  },
  {
    id: 5,
    title: 'Search Any Address',
    icon: '🔍',
    description: 'Use the search bar to jump to any address, neighborhood, or ZIP code. Hot leads in that area will auto-load!',
    features: [
      'Type any address: "123 Main St, Indianapolis"',
      'Search by ZIP: "46032" for all Carmel leads',
      'Search neighborhoods: "Broad Ripple"',
      'Results show owner data instantly'
    ],
    tip: 'Searching a ZIP loads ALL pre-scored leads in that area!',
    highlight: 'search-bar',
    category: 'search'
  },
  {
    id: 6,
    title: 'Draw Your Territory',
    icon: '✏️',
    description: 'Use the drawing tools to select specific areas. We\'ll find every property address inside your selection.',
    features: [
      '⭕ Circle - Click and drag to draw a circle',
      '⬜ Rectangle - Best for blocks and subdivisions',
      '✏️ Lasso - Freeform drawing for odd shapes',
      '📍 My Location - Jump to your current location'
    ],
    tip: 'Smaller selections = faster results. Start with 10-20 homes.',
    highlight: 'drawing-controls',
    category: 'drawing'
  },
  {
    id: 7,
    title: 'Hot Regions Panel',
    icon: '🔥',
    description: 'Click "🔥 Hot Regions" to see pre-analyzed ZIP codes ranked by lead quality. One click loads 500+ leads!',
    features: [
      'ZIPs ranked by hot lead count',
      '⚡STORM badge = recent hail damage',
      'See owner-occupied counts before loading',
      'Median income shown for each area'
    ],
    tip: 'Storm-priority ZIPs are gold - insurance companies are already paying claims!',
    highlight: 'hotspot-toggle',
    category: 'hotspots'
  },
  {
    id: 8,
    title: 'Understanding Lead Badges',
    icon: '🏷️',
    description: 'Each lead has color-coded badges showing its quality at a glance. Focus on leads with multiple badges!',
    features: [
      '🔴 HOT - High score, likely needs roof work',
      '🟢 BIG - Large roof (2500+ sqft = bigger job)',
      '🟡 OWNER - Owner-occupied (decision maker lives there)',
      '🟠 STORM - Property in recent storm path'
    ],
    tip: 'A lead with HOT + BIG + OWNER + STORM is a goldmine - prioritize these!',
    category: 'badges'
  },
  {
    id: 9,
    title: 'Property Details & Lookup',
    icon: '🏡',
    description: 'Click any lead to see full details. Use "Lookup Details" to get roof measurements, owner names, and cost estimates.',
    features: [
      '👤 Owner name and occupancy status',
      '📐 Actual roof sqft (measured from satellite)',
      '💵 Roof replacement cost estimates',
      '📅 Year built and property condition'
    ],
    tip: 'Enriched leads close at 2x the rate - always lookup before knocking!',
    category: 'details'
  },
  {
    id: 10,
    title: 'Save to CRM',
    icon: '📋',
    description: 'Never lose a lead! Save your best prospects to the built-in CRM for follow-up and tracking.',
    features: [
      'Select leads with checkboxes',
      'Click "💾 Save" to add to CRM',
      'Click "📋 CRM" in header to view saved leads',
      'Add tags and notes to track status'
    ],
    tip: 'Saved leads sync across devices - start on desktop, follow up on mobile!',
    highlight: 'crm-btn',
    category: 'crm'
  },
  {
    id: 11,
    title: 'Export Your Leads',
    icon: '📤',
    description: 'Export leads to CSV for your own CRM, or generate professional door-hanger flyers with property-specific details.',
    features: [
      '📊 CSV Export - Full data for any CRM',
      '📄 Print Flyers - Personalized door hangers',
      '📋 Copy to Clipboard - Quick address list',
      '🖨️ DOCX Flyers - Editable Word documents'
    ],
    tip: 'Flyers with the owner\'s name and roof estimate convert 40% better!',
    category: 'export'
  },
  {
    id: 12,
    title: 'Zoom Level Tips',
    icon: '🔬',
    description: 'The zoom debugger in the bottom-right shows what\'s visible at each zoom level. Zoom out to see heatmaps, zoom in to see individual properties.',
    features: [
      'Zoom 8-10: Regional heatmap view',
      'Zoom 10-12: City view with zone markers',
      'Zoom 12-14: Neighborhood clusters',
      'Zoom 16+: Individual property markers'
    ],
    tip: 'Zoom to level 11-12 to see the storm heatmap overlay!',
    category: 'zoom'
  },
  {
    id: 13,
    title: "You're Ready to Go!",
    icon: '🚀',
    description: 'You now know everything to find and close more roofing jobs. Start by exploring a storm zone or searching your favorite neighborhood!',
    features: [
      '1. Click a Storm Zone to navigate there',
      '2. Use filters to find the best leads',
      '3. Draw a selection or load a Hot Region',
      '4. Lookup details and save to CRM',
      '5. Export and start knocking doors!'
    ],
    tip: 'Pro roofers using this app close 3x more jobs. Go get \'em! 💪',
    category: 'final'
  }
];

function OnboardingGuide({ isVisible, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showAllSteps, setShowAllSteps] = useState(false);

  if (!isVisible) return null;

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;
  const isFirst = currentStep === 0;

  const handleNext = () => {
    if (isLast) {
      localStorage.setItem('leadgen_onboarded', 'true');
      onComplete?.();
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('leadgen_onboarded', 'true');
    onClose();
  };

  const handleJumpToStep = (index) => {
    setCurrentStep(index);
    setShowAllSteps(false);
  };

  // Category colors for visual grouping
  const getCategoryColor = (category) => {
    const colors = {
      intro: '#6366f1',
      storms: '#f97316',
      navigation: '#22c55e',
      filters: '#3b82f6',
      search: '#8b5cf6',
      drawing: '#ec4899',
      hotspots: '#ef4444',
      badges: '#eab308',
      details: '#14b8a6',
      crm: '#6366f1',
      export: '#84cc16',
      zoom: '#06b6d4',
      final: '#f59e0b'
    };
    return colors[category] || '#6366f1';
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal-v2">
        {/* Header with progress */}
        <div className="onboarding-header">
          <div className="onboarding-progress-bar">
            <div
              className="onboarding-progress-fill"
              style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
            />
          </div>
          <div className="onboarding-step-counter">
            Step {currentStep + 1} of {STEPS.length}
          </div>
          <button className="onboarding-close-btn" onClick={handleSkip} title="Skip tour">
            ×
          </button>
        </div>

        {/* Step Navigation Dots */}
        <div className="onboarding-dots">
          {STEPS.map((s, i) => (
            <button
              key={i}
              className={`onboarding-dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}`}
              onClick={() => handleJumpToStep(i)}
              title={s.title}
              style={{
                '--dot-color': getCategoryColor(s.category)
              }}
            />
          ))}
        </div>

        {/* Main Content */}
        <div className="onboarding-body">
          {/* Icon */}
          <div
            className="onboarding-icon"
            style={{ background: `linear-gradient(135deg, ${getCategoryColor(step.category)}, ${getCategoryColor(step.category)}dd)` }}
          >
            {step.icon}
          </div>

          {/* Title */}
          <h2 className="onboarding-title-v2">{step.title}</h2>

          {/* Description */}
          <p className="onboarding-description-v2">{step.description}</p>

          {/* Features List */}
          {step.features && (
            <div className="onboarding-features">
              {step.features.map((feature, i) => (
                <div key={i} className="onboarding-feature">
                  <span className="feature-check">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          )}

          {/* Pro Tip */}
          {step.tip && (
            <div className="onboarding-tip-v2">
              <span className="tip-icon-v2">💡</span>
              <span>{step.tip}</span>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="onboarding-footer">
          <button className="onboarding-skip-btn" onClick={handleSkip}>
            Skip Tour
          </button>

          <div className="onboarding-nav-btns">
            {!isFirst && (
              <button className="onboarding-nav-btn prev" onClick={handlePrev}>
                ← Back
              </button>
            )}
            <button className="onboarding-nav-btn next" onClick={handleNext}>
              {isLast ? 'Start Using App 🚀' : 'Next →'}
            </button>
          </div>
        </div>

        {/* View All Steps Toggle */}
        <button
          className="onboarding-view-all"
          onClick={() => setShowAllSteps(!showAllSteps)}
        >
          {showAllSteps ? 'Hide Steps' : 'View All Steps'}
        </button>

        {/* All Steps Dropdown */}
        {showAllSteps && (
          <div className="onboarding-all-steps">
            {STEPS.map((s, i) => (
              <button
                key={i}
                className={`onboarding-step-item ${i === currentStep ? 'current' : ''} ${i < currentStep ? 'completed' : ''}`}
                onClick={() => handleJumpToStep(i)}
              >
                <span className="step-item-icon">{s.icon}</span>
                <span className="step-item-title">{s.title}</span>
                {i < currentStep && <span className="step-item-check">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OnboardingGuide;
